import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Status = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddStatusModal, setShowAddStatusModal] = useState(false);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [statusName, setStatusName] = useState('');
  const [selectedStatus, setSelectedStatus] = useState({ StatusId: null, StatusName: '' });
  const [statuses, setStatuses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all statuses
  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/status/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const mappedStatuses = response.data.map(status => ({
        StatusId: status.id,
        StatusName: status.name,
      }));
      setStatuses(mappedStatuses);
      setFilteredData(mappedStatuses);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch statuses.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Add a new status
  const handleSave = async (e) => {
    e.preventDefault();
    if (!statusName) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Status Name is required.');
      setOpenSnackbar(true);
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/master/status/`,
        { name: statusName },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const newStatus = {
        StatusId: response.data.id,
        StatusName: response.data.name,
      };
      setStatuses([...statuses, newStatus]);
      setFilteredData([...filteredData, newStatus]);
      setShowAddStatusModal(false);
      setSnackbarMessage('Status added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setStatusName('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to add status.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Delete a status
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/status/${selectedStatus.StatusId}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const updatedStatuses = statuses.filter((status) => status.StatusId !== selectedStatus.StatusId);
      setStatuses(updatedStatuses);
      setFilteredData(updatedStatuses);
      setShowDeleteModal(false);
      setSnackbarMessage('Status deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete status.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Edit a status
  const handleUpdate = async () => {
    if (!statusName) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Status Name is required.');
      setOpenSnackbar(true);
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/master/status/${selectedStatus.StatusId}/`,
        { name: statusName },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const updatedStatus = {
        StatusId: response.data.id,
        StatusName: response.data.name,
      };
      const updatedStatuses = statuses.map((status) =>
        status.StatusId === selectedStatus.StatusId ? updatedStatus : status
      );
      setStatuses(updatedStatuses);
      setFilteredData(updatedStatuses);
      setShowEditStatusModal(false);
      setSnackbarMessage('Status updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setStatusName('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to update status.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  const handleAddStatus = () => {
    setStatusName('');
    setShowAddStatusModal(true);
  };

  const handleEdit = (status) => {
    setSelectedStatus({ StatusId: status.StatusId, StatusName: status.StatusName });
    setStatusName(status.StatusName);
    setShowEditStatusModal(true);
  };

  const handleShowDeleteModal = (StatusId, StatusName) => {
    setSelectedStatus({ StatusId, StatusName });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleReset = () => {
    setStatusName('');
  };

  const handleCancel = () => {
    setShowEditStatusModal(false);
    setStatusName('');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
    } else {
      fetchStatuses();
    }
  }, [navigate]);

  useEffect(() => {
    setFilteredData(
      statuses.filter((status) =>
        status.StatusName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, statuses]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by Status"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddStatus}
            sx={{
              ml: 2,
              width: '150px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD STATUS
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((status, index) => (
                    <TableRow
                      key={status.StatusId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{status.StatusName}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(status)}
                          sx={{
                            color: theme.palette.primary.light,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleShowDeleteModal(status.StatusId, status.StatusName)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={3} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedStatus.StatusName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Status Modal */}
      <Modal
        open={showAddStatusModal}
        onClose={() => setShowAddStatusModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add Status
            </Typography>
            <IconButton onClick={() => setShowAddStatusModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <TextField
  label="Status Name"
  placeholder="e.g. Pending, Interested, In Progress"
  fullWidth
  required
  value={statusName}
  onChange={(e) => setStatusName(e.target.value)}
  helperText="Enter a meaningful status such as Pending, Interested, or In Progress"
/>
          
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!statusName ? "Enter Status" : ""} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!statusName}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleReset}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Edit Status Modal */}
      <Modal
        open={showEditStatusModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit Status
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <TextField
              label="Status"
              variant="outlined"
              fullWidth
              value={statusName}
              onChange={(e) => setStatusName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!statusName ? "Enter Status" : ""} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleUpdate}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!statusName}
                  >
                    UPDATE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Status Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            {/* Section 1: Add Status */}
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
            >
              ➤ Follow these steps to add a new status:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Click the <strong>"ADD STATUS"</strong> button at the top right to open the Add Status dialog.
              </li>
              <li>
                Enter the <strong>Status</strong> in the provided text field.
              </li>
              <li>
                Before clicking <strong>"SAVE"</strong>, review the entered information for accuracy.
              </li>
              <li>
                Click <strong>"SAVE"</strong> to add the status. A success notification will confirm the addition.
              </li>
              <li>
                The new status will appear in the Status List table.
              </li>
              <li>
                To clear the form without saving, click <strong>"RESET"</strong>.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Status is a required field.
            </Typography>

            {/* Section 2: Edit Status */}
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to update an existing status:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Locate the status you want to update in the Status List table.
              </li>
              <li>
                Click the <strong>Edit</strong> icon (pencil) in the Action column to open the Edit Status dialog.
              </li>
              <li>
                Update the <strong>Status</strong> as needed.
              </li>
              <li>
                Before clicking <strong>"UPDATE"</strong>, ensure the Status field is filled.
              </li>
              <li>
                Click <strong>"UPDATE"</strong> to save your changes. A success notification will confirm the update.
              </li>
              <li>
                To exit without saving, click <strong>"CANCEL"</strong>.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Status is a required field.
            </Typography>

            {/* Section 3: Delete Status */}
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to delete a status:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Find the status you want to delete in the Status List table.
              </li>
              <li>
                Click the <strong>Delete</strong> icon (trash) in the Action column.
              </li>
              <li>
                Confirm the deletion by clicking <strong>"Yes, Delete"</strong> in the confirmation dialog.
              </li>
              <li>
                The status will be removed from the list, and a success notification will appear.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Deleted statuses cannot be recovered. Proceed with caution.
            </Typography>

            {/* Section 4: Search Statuses */}
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to search for a status:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Use the search bar at the top left of the Status List.
              </li>
              <li>
                Enter the Status (or part of it) to filter the table.
              </li>
              <li>
                The table will update automatically to show matching statuses.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: The search is case-insensitive and filters by Status.
            </Typography>

            {/* Section 5: Pagination */}
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to navigate the status list:
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                Use the pagination controls at the bottom to navigate through pages of statuses.
              </li>
              <li>
                Click the left (<strong>&lt;&lt;</strong>) or right (<strong>&gt;&gt;</strong>) arrows to move between pages.
              </li>
              <li>
                Select a specific page number to jump directly to that page.
              </li>
              <li>
                Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: The table displays only the selected number of rows per page.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>

    </Container>
  );
};

export default Status;
